import {
  Box,
  Flex,
  Stack,
  Text,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Divider,
} from '@chakra-ui/react';
import { useState } from 'react';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { RetrievedContentDataFragment } from '../../generated/graphql';

interface ChatBubbleProps {
  senderUser: boolean;
  content: string;
  screenSize?: 'full' | 'small';
  name?: string;
  retrievedContent?: RetrievedContentDataFragment[];
}

const ChatBubble = ({
  senderUser,
  content,
  name,
  retrievedContent,
}: ChatBubbleProps) => {
  const [isSourcesModalOpen, setIsSourcesModalOpen] = useState(false);

  return (
    <>
      <Stack spacing={1} w="100%">
        {name && (
          <Flex width="full" justifyContent="flex-start">
            <HelivanText
              variant="smallBody"
              colorOverride={HelivanColor.text.gray}
            >
              {name}
            </HelivanText>
          </Flex>
        )}
        <Flex
          justifyContent={senderUser ? 'flex-end' : 'flex-start'}
          mb={4}
          w="100%"
        >
          <Box
            width="75%"
            bg={senderUser ? 'white' : '#F6F7F9'}
            p="10px"
            position="relative"
            borderRadius="12px"
            textAlign="left"
            border={senderUser ? '1px solid #D7D7D7' : undefined}
            cursor={retrievedContent ? 'pointer' : 'default'}
            _hover={
              retrievedContent
                ? {
                    bg: senderUser ? '#F8F8F8' : '#ECEEF2',
                  }
                : undefined
            }
            onClick={() => {
              if (retrievedContent) {
                setIsSourcesModalOpen(true);
              }
            }}
          >
            <Tooltip
              isDisabled={!retrievedContent}
              label="click to see response sources"
              placement="top"
              hasArrow
            >
              <Text color="black" fontSize="16px">
                {content}
              </Text>
            </Tooltip>
          </Box>
        </Flex>
      </Stack>

      {/* Sources Modal */}
      <Modal
        isOpen={isSourcesModalOpen}
        onClose={() => setIsSourcesModalOpen(false)}
        size="xl"
      >
        <ModalOverlay onClick={() => setIsSourcesModalOpen(false)} />
        <ModalContent maxH="80vh" overflowY="auto">
          <ModalHeader>Response Sources</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              {retrievedContent?.map((item, index) => (
                <Box
                  key={index}
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor="gray.200"
                  bg="gray.50"
                >
                  <Stack spacing={2}>
                    <Flex justifyContent="space-between" alignItems="center">
                      <HelivanText variant="largeBody+" colorOverride="black">
                        Source: {item.source}
                      </HelivanText>
                      <Box
                        px={2}
                        py={1}
                        borderRadius="md"
                        bg={
                          item.score && item.score > 80
                            ? 'green.100'
                            : item.score && item.score > 50
                              ? 'yellow.100'
                              : 'red.100'
                        }
                      >
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color={
                            item.score && item.score > 80
                              ? 'green.700'
                              : item.score && item.score > 50
                                ? 'yellow.700'
                                : 'red.700'
                          }
                        >
                          Score: {Math.round(item.score || 0)}
                        </Text>
                      </Box>
                    </Flex>
                    <HelivanText variant="smallBody" colorOverride="gray.600">
                      Posted: {item.postedAt}
                    </HelivanText>
                    <Divider />
                    <Text fontSize="sm" color="gray.700">
                      {item.text}
                    </Text>
                  </Stack>
                </Box>
              ))}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ChatBubble;
